import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api";
import { useAuth } from "../AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { Download, Plus, Trash2, MessageCircle, Calendar, ChevronLeft, Search, UserPlus, FileText, BarChart3, CheckCircle2, Clock, XCircle, Stethoscope, FileCheck, HelpCircle, Coffee, Moon, Sunrise, Camera } from "lucide-react";
import { toast } from "sonner";
import { WorkerPhotoModal } from "../components/WorkerPhotoModal";

const POLL_MS = 15000;

const STATUSES = [
  { key: "Present",  label: "Present", color: "emerald", icon: CheckCircle2 },
  { key: "Late",     label: "Late",    color: "amber",   icon: Clock },
  { key: "Absent",   label: "Absent",  color: "red",     icon: XCircle },
  { key: "Medical",  label: "Medical", color: "sky",     icon: Stethoscope },
  { key: "MC",       label: "MC",      color: "violet",  icon: FileCheck },
  { key: "MIA",      label: "MIA",     color: "rose",    icon: HelpCircle },
  { key: "Off",      label: "Off",     color: "slate",   icon: Coffee },
  { key: "NG",       label: "NG",      color: "indigo",  icon: Moon,     sub: "Night — pending" },
  { key: "NG IN",    label: "NG IN",   color: "teal",    icon: Sunrise,  sub: "Night — confirmed in" },
];

const STATUS_CLASSES = {
  Present:  "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  Late:     "bg-amber-500/15 text-amber-300 border-amber-500/30",
  Absent:   "bg-red-500/15 text-red-300 border-red-500/30",
  Medical:  "bg-sky-500/15 text-sky-300 border-sky-500/30",
  MC:       "bg-violet-500/15 text-violet-300 border-violet-500/30",
  MIA:      "bg-rose-500/15 text-rose-300 border-rose-500/30",
  Off:      "bg-slate-500/15 text-slate-300 border-slate-500/30",
  NG:       "bg-indigo-500/15 text-indigo-300 border-indigo-500/30",
  "NG IN":  "bg-teal-500/15 text-teal-300 border-teal-500/30",
};

function todayIso() {
  const d = new Date(); const m = String(d.getMonth()+1).padStart(2,"0"); const dd = String(d.getDate()).padStart(2,"0");
  return `${d.getFullYear()}-${m}-${dd}`;
}

export default function RosterView() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const isOwner = !!user?.is_owner;
  const [companies, setCompanies] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [date, setDate] = useState(todayIso());
  const [workers, setWorkers] = useState([]);
  const [filter, setFilter] = useState("");
  const [addWorkerOpen, setAddWorkerOpen] = useState(false);
  const [removeTarget, setRemoveTarget] = useState(null);
  const [removeReason, setRemoveReason] = useState("");
  // Non-owner "Request removal" flow + owner-visible pending list.
  const [deletionRequests, setDeletionRequests] = useState([]);
  const [reviewTarget, setReviewTarget] = useState(null); // { mode, req }
  const [reviewNote, setReviewNote] = useState("");
  const [reportOpen, setReportOpen] = useState(false);
  const [waOpen, setWaOpen] = useState(false);
  const [mobileViewWorkers, setMobileViewWorkers] = useState(false);
  // Photo modal target (a worker, or null when closed). Mounted once at the
  // top so we don't re-mount per row.
  const [photoTarget, setPhotoTarget] = useState(null);

  // Load companies + summaries
  const loadCompanies = useCallback(async () => {
    try {
      const { data } = await api.get("/roster/companies", { params: { date } });
      // Kenvue + Ceva moved to the J&J module (Supervisor Manual tab).
      // Filter them out of Kedatangan so all attendance for those shifts is
      // recorded via J&J instead.
      setCompanies((data || []).filter((c) =>
        !(c.name?.startsWith("Kenvue") || c.name?.startsWith("Ceva"))));
      if (!selectedId && data.length > 0) setSelectedId(data[0].id);
    } catch (e) { /* silent */ }
  }, [date, selectedId]);

  useEffect(() => { loadCompanies(); }, [loadCompanies]);
  useEffect(() => { const t = setInterval(loadCompanies, POLL_MS); return () => clearInterval(t); }, [loadCompanies]);

  // Load workers for selected company+date
  const loadWorkers = useCallback(async () => {
    if (!selectedId) return;
    try {
      const { data } = await api.get(`/roster/companies/${selectedId}/workers`, { params: { date } });
      setWorkers(data.workers || []);
    } catch (e) { /* silent */ }
  }, [selectedId, date]);
  useEffect(() => { loadWorkers(); }, [loadWorkers]);

  // Pending removal-request list (owner sees all; non-owner sees their own).
  const loadDeletionRequests = useCallback(async () => {
    try {
      const { data } = await api.get("/worker-deletion-requests?status=Pending&target=roster");
      setDeletionRequests(data);
    } catch (_) { /* silent */ }
  }, []);
  useEffect(() => { loadDeletionRequests(); }, [loadDeletionRequests]);

  const selectedCompany = useMemo(
    () => companies.find((c) => c.id === selectedId),
    [companies, selectedId]
  );

  const filtered = useMemo(() => {
    if (!filter.trim()) return workers;
    const q = filter.toLowerCase();
    return workers.filter((w) => w.name.toLowerCase().includes(q) || (w.code||"").toLowerCase().includes(q));
  }, [workers, filter]);

  const markStatus = async (worker, status) => {
    // Optimistic update
    setWorkers((prev) => prev.map((w) => w.id === worker.id
      ? { ...w, attendance: { ...(w.attendance||{}), status } } : w));
    try {
      await api.post("/roster/attendance", {
        company_id: selectedId,
        date,
        entries: [{
          worker_id: worker.id,
          status,
          check_in: worker.attendance?.check_in || "",
          check_out: worker.attendance?.check_out || "",
          notes: worker.attendance?.notes || "",
        }],
      });
      loadCompanies();
    } catch (e) {
      toast.error("Failed to save — reloading");
      loadWorkers();
    }
  };

  return (
    <div className="space-y-4" data-testid="roster-page">
        {/* Top bar */}
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <h1 className="text-xl font-semibold tracking-tight">Roster · Worker Attendance</h1>
            <span className="text-xs num text-slate-500">· {companies.length} companies · {companies.reduce((a,c)=>a+(c.worker_count||0),0)} workers</span>
          </div>
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-slate-500" />
            <Input type="date" value={date} onChange={(e)=>setDate(e.target.value)}
              className="bg-[#0f1626] border-slate-800 h-9 w-[160px] num"
              data-testid="roster-date" />
            {isAdmin && (
              <Button
                size="sm"
                variant="outline"
                className="h-9 border-slate-700 text-xs text-slate-200 hover:bg-slate-800"
                data-testid="roster-sync-staff-access"
                onClick={async () => {
                  if (!window.confirm("Grant every active company to every staff member with Kedatangan access?\n\nThis only ADDS — it does not revoke anyone.")) return;
                  try {
                    const { data } = await api.post("/roster/companies/sync-staff-access");
                    toast.success(`Synced ${data.companies} companies to ${data.users_updated} staff`);
                  } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
                }}>
                Sync staff access
              </Button>
            )}
          </div>
        </div>

        {/* Owner-only pending removal-requests banner */}
        {isOwner && deletionRequests.length > 0 && (
          <div className="rounded-lg border border-rose-500/40 bg-rose-500/5 p-3"
               data-testid="roster-pending-deletion-requests">
            <div className="text-sm font-medium text-rose-200 flex items-center gap-2 mb-2">
              <XCircle className="w-4 h-4" />
              {deletionRequests.length} pending worker removal request{deletionRequests.length !== 1 ? "s" : ""}
            </div>
            <div className="space-y-2">
              {deletionRequests.map((r) => (
                <div key={r.id} className="rounded border border-slate-800 bg-slate-900/40 p-2.5"
                     data-testid={`roster-deletion-req-${r.id}`}>
                  <div className="flex items-start justify-between gap-2 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap text-sm">
                        <span className="text-slate-100 font-medium">{r.worker_name}</span>
                        <span className="text-[11px] text-slate-500 num">· {r.worker_company || "—"}</span>
                      </div>
                      <div className="text-[11px] text-slate-500 num mt-0.5 flex items-center gap-2 flex-wrap">
                        <span className="inline-flex items-center gap-1">
                          <span className="w-2 h-2 rounded-full" style={{ background: r.requested_by_color || "#64748b" }} />
                          {r.requested_by_name}
                        </span>
                        <span className="text-slate-600">·</span>
                        <Clock className="w-3 h-3 inline" /> {(r.requested_at || "").slice(0, 16).replace("T", " ")}
                      </div>
                      <div className="text-xs text-slate-300 italic mt-1">"{r.reason}"</div>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <Button size="sm" onClick={() => setReviewTarget({ mode: "approve", req: r })}
                        className="h-8 bg-rose-600 hover:bg-rose-500"
                        data-testid={`roster-req-approve-${r.id}`}>
                        <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Approve
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setReviewTarget({ mode: "reject", req: r })}
                        className="h-8 border-slate-600 text-slate-200 hover:bg-slate-800"
                        data-testid={`roster-req-reject-${r.id}`}>
                        <XCircle className="w-3.5 h-3.5 mr-1" /> Reject
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-4">
          {/* COMPANY SIDEBAR */}
          <div className={`rounded-xl border hairline surface-1 overflow-hidden ${mobileViewWorkers ? "hidden lg:block" : ""}`}>
            <div className="px-3 py-2 border-b hairline text-xs uppercase tracking-wider text-slate-400 flex items-center justify-between">
              <span>Companies</span>
              {isAdmin && <AddCompanyButton onDone={loadCompanies} />}
            </div>
            <div className="max-h-[72vh] overflow-y-auto scrollbar-thin" data-testid="roster-companies">
              {companies.length === 0 && (
                <div className="p-6 text-center text-xs text-slate-500 leading-relaxed" data-testid="roster-empty-access">
                  {isAdmin ? (
                    <>No companies yet. Click <span className="text-slate-300">+</span> to add one.</>
                  ) : (
                    <>You have no roster access yet.<br/>Ask admin to assign you companies in <span className="text-slate-300">Staff Management → Kedatangan · company access</span>.</>
                  )}
                </div>
              )}
              {companies.map((c) => {
                const total = c.worker_count || 0;
                const marked = c.marked || 0;
                const present = c.summary?.Present || 0;
                const active = selectedId === c.id;
                return (
                  <button key={c.id} type="button"
                    onClick={() => { setSelectedId(c.id); setMobileViewWorkers(true); }}
                    className={`w-full text-left px-3 py-2.5 border-b border-slate-900/70 transition-colors ${
                      active ? "bg-emerald-600/15 border-l-2 border-l-emerald-500" : "hover:bg-white/[0.03]"
                    }`}
                    data-testid={`company-${c.code || c.id}`}>
                    <div className="flex items-center justify-between gap-2">
                      <div className="font-medium text-sm truncate">{c.name}</div>
                      <div className="text-[10px] num px-1.5 py-0.5 rounded-full border hairline surface-2 shrink-0">
                        {total}
                      </div>
                    </div>
                    <div className="mt-1 flex items-center gap-1.5 text-[11px] text-slate-500">
                      <span className="text-emerald-400">✓ {present}</span>
                      <span className="text-slate-700">·</span>
                      <span className="num">{marked}/{total} marked</span>
                    </div>
                    {total > 0 && (
                      <div className="mt-1.5 h-1 rounded-full bg-slate-900 overflow-hidden">
                        <div className="h-full bg-emerald-500" style={{ width: `${(marked/total)*100}%` }} />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* WORKERS PANE */}
          <div className={`rounded-xl border hairline surface-1 overflow-hidden ${mobileViewWorkers ? "" : "hidden lg:block"}`}>
            {!selectedCompany ? (
              <div className="p-8 text-center text-slate-500 text-sm">Select a company to view workers.</div>
            ) : (
              <>
                <div className="p-4 border-b hairline space-y-3">
                  <div className="flex items-start justify-between gap-2 flex-wrap">
                    <div className="flex items-center gap-2 min-w-0">
                      <Button size="sm" variant="ghost" className="lg:hidden -ml-2"
                        onClick={() => setMobileViewWorkers(false)}>
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      <div className="min-w-0">
                        <div className="text-[11px] uppercase tracking-wider text-slate-500">{date}</div>
                        <h2 className="text-lg font-semibold tracking-tight truncate">{selectedCompany.name}</h2>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Button size="sm" variant="outline" className="border-slate-700 h-8"
                        onClick={() => setWaOpen(true)} data-testid="roster-whatsapp-btn">
                        <MessageCircle className="w-3.5 h-3.5 mr-1 text-emerald-400" /> WhatsApp
                      </Button>
                      <Button size="sm" variant="outline" className="border-slate-700 h-8"
                        onClick={() => setReportOpen(true)} data-testid="roster-report-btn">
                        <FileText className="w-3.5 h-3.5 mr-1" /> Monthly
                      </Button>
                      <Link to="/reports">
                        <Button size="sm" variant="outline" className="border-slate-700 h-8"
                          data-testid="roster-reports-btn">
                          <BarChart3 className="w-3.5 h-3.5 mr-1 text-blue-300" /> Reports
                        </Button>
                      </Link>
                      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-500 text-white h-8"
                        onClick={() => setAddWorkerOpen(true)} data-testid="roster-add-worker-btn">
                        <UserPlus className="w-3.5 h-3.5 mr-1" /> Add worker
                      </Button>
                    </div>
                  </div>

                  {/* Summary pills */}
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {STATUSES.map((s) => {
                      const n = selectedCompany.summary?.[s.key] || 0;
                      return (
                        <span key={s.key}
                          className={`text-[11px] px-2 py-0.5 rounded-full border ${STATUS_CLASSES[s.key]} num`}>
                          {s.label} {n}
                        </span>
                      );
                    })}
                    <span className="text-[11px] px-2 py-0.5 rounded-full border border-slate-700 text-slate-400 num">
                      Unmarked {selectedCompany.summary?.Unmarked || 0}
                    </span>
                  </div>

                  <div className="relative">
                    <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-slate-500" />
                    <Input value={filter} onChange={(e)=>setFilter(e.target.value)}
                      placeholder="Filter workers…" className="pl-8 bg-[#0f1626] border-slate-800 h-9" />
                  </div>
                </div>

                <div className="max-h-[56vh] overflow-y-auto scrollbar-thin divide-y divide-slate-900/70"
                  data-testid="roster-workers-list">
                  {filtered.length === 0 ? (
                    <div className="p-8 text-center text-slate-500 text-sm">No workers match.</div>
                  ) : filtered.map((w, idx) => (
                    <WorkerRow key={w.id} worker={w} idx={idx+1}
                      companyName={selectedCompany?.name || ""}
                      onStatus={(st) => markStatus(w, st)}
                      onShowPhoto={() => setPhotoTarget({ ...w, company_name: selectedCompany?.name || "" })}
                      onDetails={() => setRemoveTarget(null) /* placeholder */}
                      onEdit={async (patch) => {
                        setWorkers((prev) => prev.map((x) => x.id === w.id
                          ? { ...x, attendance: { ...(x.attendance||{}), ...patch } } : x));
                        try {
                          await api.post("/roster/attendance", {
                            company_id: selectedId, date,
                            entries: [{
                              worker_id: w.id,
                              status: patch.status ?? (w.attendance?.status || "Present"),
                              check_in: patch.check_in ?? (w.attendance?.check_in || ""),
                              check_out: patch.check_out ?? (w.attendance?.check_out || ""),
                              notes: patch.notes ?? (w.attendance?.notes || ""),
                            }],
                          });
                          loadCompanies();
                        } catch { loadWorkers(); }
                      }}
                      onRemove={() => setRemoveTarget(w)}
                      canRemoveDirectly={isOwner}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        {/* DIALOGS */}
        <AddWorkerDialog open={addWorkerOpen} onOpenChange={setAddWorkerOpen}
          companyId={selectedId} onDone={() => { loadWorkers(); loadCompanies(); }} />

        <MonthlyReportDialog open={reportOpen} onOpenChange={setReportOpen}
          company={selectedCompany} />

        <WhatsAppDialog open={waOpen} onOpenChange={setWaOpen}
          companyId={selectedId} companyName={selectedCompany?.name} date={date} />

        <WorkerPhotoModal
          open={!!photoTarget}
          onOpenChange={(v) => { if (!v) setPhotoTarget(null); }}
          worker={photoTarget}
          onChanged={() => { loadWorkers(); }}
        />

        <AlertDialog open={!!removeTarget} onOpenChange={(v) => { if (!v) { setRemoveTarget(null); setRemoveReason(""); } }}>
          <AlertDialogContent className="bg-[#0f1626] border-slate-800 text-slate-100">
            <AlertDialogHeader>
              <AlertDialogTitle>
                {isOwner
                  ? `Terminate "${removeTarget?.name}"?`
                  : `Request removal of "${removeTarget?.name}"?`}
              </AlertDialogTitle>
              <AlertDialogDescription className="text-slate-400">
                {isOwner ? (
                  <>
                    Worker will be marked as <strong className="text-red-300">terminated</strong>.
                    They keep appearing in past monthly reports — showing exactly when they stopped working —
                    and stop appearing in new daily attendance.
                    <br /><br />
                    Why are they leaving? (this is shown on the report)
                  </>
                ) : (
                  <>
                    You cannot remove workers directly. This sends a request to the
                    <strong className="text-amber-300"> owner</strong> for approval. If approved,
                    the worker will be terminated (past reports preserved). You'll get a
                    WhatsApp when the owner decides.
                    <br /><br />
                    Reason for removal (required):
                  </>
                )}
              </AlertDialogDescription>
              <Input
                value={removeReason}
                onChange={(e) => setRemoveReason(e.target.value)}
                placeholder={isOwner
                  ? "e.g. ran away, contract ended, fired, transferred"
                  : "e.g. Terminated last month, no return. Passport returned."}
                className="bg-[#0a0e1a] border-slate-800 mt-3"
                data-testid="terminate-reason"
              />
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="border-slate-700">Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={async () => {
                  if (isOwner) {
                    try {
                      await api.delete(`/roster/workers/${removeTarget.id}`, {
                        params: { reason: removeReason.trim() },
                      });
                      toast.success("Worker terminated");
                      setRemoveTarget(null); setRemoveReason("");
                      loadWorkers(); loadCompanies();
                    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
                  } else {
                    if (!removeReason.trim()) { toast.error("Reason is required"); return; }
                    try {
                      await api.post(`/roster/workers/${removeTarget.id}/request-deletion`,
                                     { reason: removeReason.trim() });
                      toast.success("Removal request sent to owner");
                      setRemoveTarget(null); setRemoveReason("");
                    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
                  }
                }} className={isOwner ? "bg-red-600 hover:bg-red-500" : "bg-amber-600 hover:bg-amber-500"}
                data-testid="terminate-confirm">
                {isOwner ? "Mark terminated" : "Send request to owner"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Owner-only review dialog for pending removal requests */}
        {reviewTarget && (
          <AlertDialog open onOpenChange={() => { setReviewTarget(null); setReviewNote(""); }}>
            <AlertDialogContent className="bg-[#0f1626] border-slate-800 text-slate-100"
                                data-testid={`roster-review-${reviewTarget.mode}-dialog`}>
              <AlertDialogHeader>
                <AlertDialogTitle>
                  {reviewTarget.mode === "approve" ? "Approve removal?" : "Reject removal?"}
                </AlertDialogTitle>
                <AlertDialogDescription className="text-slate-400">
                  Worker: <strong className="text-slate-100">{reviewTarget.req.worker_name}</strong>
                  <span className="text-slate-600"> · {reviewTarget.req.worker_company || "—"}</span>
                  <br />
                  Requested by <span className="text-slate-200">{reviewTarget.req.requested_by_name}</span>
                  <span className="text-slate-600"> · {(reviewTarget.req.requested_at || "").slice(0, 16).replace("T", " ")}</span>
                  <br /><br />
                  <span className="text-slate-200 italic">"{reviewTarget.req.reason}"</span>
                  {reviewTarget.mode === "approve" && (
                    <div className="mt-3 text-[11px] text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded p-2">
                      ⚠ Approving will mark this worker as <strong>terminated</strong>. Past monthly reports are preserved.
                    </div>
                  )}
                </AlertDialogDescription>
                <Input
                  value={reviewNote}
                  onChange={(e) => setReviewNote(e.target.value)}
                  placeholder={reviewTarget.mode === "approve" ? "Optional note" : "Reason for rejection (shown to requester)"}
                  className="bg-[#0a0e1a] border-slate-800 mt-3"
                />
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="border-slate-700">Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={async () => {
                    try {
                      await api.post(
                        `/worker-deletion-requests/${reviewTarget.req.id}/${reviewTarget.mode}`,
                        { note: reviewNote },
                      );
                      toast.success(reviewTarget.mode === "approve" ? "Approved — worker terminated" : "Rejected");
                      setReviewTarget(null); setReviewNote("");
                      loadDeletionRequests(); loadWorkers();
                    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
                  }}
                  className={reviewTarget.mode === "approve"
                    ? "bg-rose-600 hover:bg-rose-500"
                    : "bg-slate-600 hover:bg-slate-500"}
                  data-testid={`roster-review-${reviewTarget.mode}-submit`}>
                  {reviewTarget.mode === "approve" ? "Approve + Terminate" : "Reject"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>
  );
}

function WorkerRow({ worker, idx, onStatus, onEdit, onRemove, onShowPhoto, canRemoveDirectly = true }) {
  const a = worker.attendance || {};
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="px-3 py-2.5" data-testid={`worker-row-${worker.id}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium truncate flex items-center gap-2">
            <span className="text-slate-600 num text-[11px] w-7 shrink-0">#{idx}</span>
            <button type="button"
              onClick={onShowPhoto}
              title="Click to view reference photo"
              className="truncate text-left hover:text-blue-300 transition-colors inline-flex items-center gap-1.5 min-w-0"
              data-testid={`worker-name-${worker.id}`}>
              {worker.has_photo ? (
                <Camera className="w-3.5 h-3.5 text-blue-400 shrink-0" />
              ) : (
                <Camera className="w-3.5 h-3.5 text-slate-600 shrink-0 opacity-50" />
              )}
              <span className="truncate">{worker.name}</span>
            </button>
            {/* "Request removal" link beside name for non-owner staff.
                Owner keeps only the trash icon at row end (avoids double). */}
            {onRemove && !canRemoveDirectly && (
              <button
                type="button"
                onClick={onRemove}
                title="Request removal — owner will approve"
                className="shrink-0 text-[10.5px] text-amber-400/80 hover:text-amber-300 underline underline-offset-2 decoration-dotted"
                data-testid={`request-remove-inline-${worker.id}`}>
                request&nbsp;removal
              </button>
            )}
          </div>
          {(a.check_in || a.check_out || a.notes) && (
            <div className="text-[11px] text-slate-500 mt-0.5 num flex items-center gap-2 flex-wrap">
              {a.check_in && <span>in <span className="text-slate-300">{a.check_in}</span></span>}
              {a.check_out && <span>out <span className="text-slate-300">{a.check_out}</span></span>}
              {a.notes && <span className="italic text-slate-400 truncate max-w-[240px]">— {a.notes}</span>}
            </div>
          )}
        </div>

        <div className="flex items-center gap-1 flex-wrap justify-end">
          {STATUSES.map((s) => {
            const active = a.status === s.key;
            return (
              <button key={s.key} type="button" onClick={() => onStatus(s.key)}
                className={`text-[10.5px] font-medium uppercase tracking-wider px-2 py-1 rounded-md border transition-all ${
                  active ? STATUS_CLASSES[s.key] + " ring-1 ring-inset ring-emerald-400/30" : "border-slate-800 text-slate-500 hover:text-slate-200 hover:border-slate-600"
                }`}
                data-testid={`mark-${s.key.toLowerCase()}-${worker.id}`}>
                {s.label}
              </button>
            );
          })}
          <button type="button" onClick={() => setExpanded((v)=>!v)}
            className="text-[10.5px] text-slate-500 hover:text-slate-200 px-2 py-1">
            {expanded ? "Hide" : "Details"}
          </button>
          {onRemove && canRemoveDirectly && (
            <button type="button" onClick={onRemove}
              className="text-slate-600 hover:text-red-400 p-1"
              title="Terminate worker"
              data-testid={`remove-worker-${worker.id}`}>
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>
      {expanded && (
        <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-2">
          <Input value={a.check_in || ""} onChange={(e)=>onEdit({ check_in: e.target.value })}
            placeholder="Check-in (e.g. 08:05)" className="bg-[#0a0e1a] border-slate-800 h-8 num" />
          <Input value={a.check_out || ""} onChange={(e)=>onEdit({ check_out: e.target.value })}
            placeholder="Check-out (e.g. 17:30)" className="bg-[#0a0e1a] border-slate-800 h-8 num" />
          <Input value={a.notes || ""} onChange={(e)=>onEdit({ notes: e.target.value })}
            placeholder="Notes" className="bg-[#0a0e1a] border-slate-800 h-8" />
        </div>
      )}
    </div>
  );
}

function AddCompanyButton({ onDone }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const submit = async () => {
    if (!name.trim()) return;
    try {
      await api.post("/roster/companies", { name: name.trim() });
      toast.success("Company added");
      setOpen(false); setName(""); onDone?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <>
      <button type="button" onClick={()=>setOpen(true)}
        className="text-[11px] text-emerald-400 hover:text-emerald-300 flex items-center gap-1"
        data-testid="roster-add-company-btn">
        <Plus className="w-3 h-3" /> Add
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0f1626] border-slate-800 text-slate-100 max-w-md">
          <DialogHeader><DialogTitle>Add company</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <Label className="text-xs text-slate-400">Company name</Label>
            <Input autoFocus value={name} onChange={(e)=>setName(e.target.value)}
              className="bg-[#0a0e1a] border-slate-800" onKeyDown={(e)=>e.key==="Enter"&&submit()} />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={()=>setOpen(false)}>Cancel</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-500" onClick={submit}>Add</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function AddWorkerDialog({ open, onOpenChange, companyId, onDone }) {
  const [name, setName] = useState("");
  useEffect(() => { if (open) setName(""); }, [open]);
  const submit = async () => {
    if (!name.trim() || !companyId) return;
    try {
      await api.post(`/roster/companies/${companyId}/workers`, {
        company_id: companyId, name: name.trim(),
      });
      toast.success("Worker added");
      onDone?.(); onOpenChange(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0f1626] border-slate-800 text-slate-100 max-w-md">
        <DialogHeader><DialogTitle>Add worker</DialogTitle></DialogHeader>
        <div className="space-y-2">
          <Label className="text-xs text-slate-400">Full name</Label>
          <Input autoFocus value={name} onChange={(e)=>setName(e.target.value)}
            className="bg-[#0a0e1a] border-slate-800"
            onKeyDown={(e)=>e.key==="Enter"&&submit()}
            data-testid="add-worker-name" />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={()=>onOpenChange(false)}>Cancel</Button>
          <Button className="bg-emerald-600 hover:bg-emerald-500" onClick={submit}
            data-testid="add-worker-submit">Add worker</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MonthlyReportDialog({ open, onOpenChange, company }) {
  const [month, setMonth] = useState(() => todayIso().slice(0,7));
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!open || !company) return;
    (async () => {
      try {
        const { data: d } = await api.get(`/roster/companies/${company.id}/report`, { params: { month } });
        setData(d);
      } catch {}
    })();
  }, [open, company, month]);
  if (!company) return null;

  // Build array of day numbers for the month
  const daysInMonth = (() => {
    if (!month) return [];
    const [y, m] = month.split("-").map(Number);
    const d = new Date(y, m, 0).getDate();
    return Array.from({ length: d }, (_, i) => i + 1);
  })();

  // Status → 2-letter code + class. All 9 roster statuses render distinctly.
  const STATUS_CELL = {
    Present:  { ch: "P",  cls: "text-emerald-300 font-semibold" },
    Late:     { ch: "L",  cls: "text-amber-300 font-semibold" },
    Absent:   { ch: "A",  cls: "text-red-400 font-semibold" },
    Medical:  { ch: "Md", cls: "text-sky-300 font-semibold" },
    MC:       { ch: "MC", cls: "text-violet-300 font-semibold" },
    MIA:      { ch: "MIA",cls: "text-rose-300 font-semibold" },
    Off:      { ch: "Off",cls: "text-slate-400 font-semibold" },
    NG:       { ch: "NG", cls: "text-indigo-300 font-semibold" },
    "NG IN":  { ch: "Ni", cls: "text-teal-300 font-semibold" },
  };
  const cellFor = (status) => STATUS_CELL[status] || { ch: "·", cls: "text-slate-700" };

  // 7 per-status summary columns shown to the right of the matrix.
  const SUMMARY_COLS = [
    { key: "Present", short: "P",   cls: "text-emerald-300" },
    { key: "NG IN",   short: "Ni",  cls: "text-teal-300" },
    { key: "Late",    short: "L",   cls: "text-amber-300" },
    { key: "Absent",  short: "A",   cls: "text-red-400" },
    { key: "Medical", short: "Md",  cls: "text-sky-300" },
    { key: "MC",      short: "MC",  cls: "text-violet-300" },
    { key: "MIA",     short: "MIA", cls: "text-rose-300" },
  ];

  // Compute column totals from worker rows (so TOTAL row stays in sync if
  // we ever change the row data shape).
  const rows = data?.workers || [];
  const sumCol = (key) => rows.reduce((s, w) => s + (w.counts?.[key] || 0), 0);

  // Pretty month label (e.g. "April 2026") for the title.
  const monthLabel = (() => {
    if (!month) return "";
    try {
      const [y, m] = month.split("-").map(Number);
      return new Date(y, m - 1, 1).toLocaleString("en-GB", { month: "long", year: "numeric" });
    } catch { return month; }
  })();

  // Pretty date for the printed-on stamp
  const todayLabel = new Date().toLocaleDateString("en-GB", {
    day: "2-digit", month: "short", year: "numeric"
  });

  const recordedByLine = (data?.recorded_by || [])
    .map((r) => `${r.name} (${r.count})`).join(" · ") || "—";

  const handlePrint = () => window.print();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0f1626] border-slate-800 text-slate-100 max-w-[97vw] max-h-[94vh] overflow-hidden flex flex-col p-0">
        {/* Inline print styles — scoped via .monthly-report-print so the rest of
            the app keeps its dark UI when the user prints. */}
        <style>{`
          @media print {
            @page { size: A4 landscape; margin: 10mm; }
            body * { visibility: hidden !important; }
            .monthly-report-print, .monthly-report-print * { visibility: visible !important; }
            .monthly-report-print {
              position: fixed; inset: 0; background: white; color: #0f172a;
              font-family: "DM Sans", sans-serif; padding: 8mm 10mm;
              max-width: 100%; max-height: 100%; overflow: visible;
            }
            .monthly-report-print .no-print { display: none !important; }
            .monthly-report-print .print-only { display: block !important; }
            .monthly-report-print table.matrix th,
            .monthly-report-print table.matrix td {
              border: 1px solid #cbd5e1 !important; color: #0f172a !important;
              background: white !important;
            }
            .monthly-report-print table.matrix thead th {
              background: #0f172a !important; color: white !important;
            }
            .monthly-report-print table.matrix tfoot td {
              background: #0f172a !important; color: white !important;
              font-weight: 700;
            }
            .monthly-report-print .banner { background: #f8fafc !important; }
            .monthly-report-print .banner .label { color: #0f172a !important; }
            .monthly-report-print .banner .value-ent { color: #047857 !important; }
            .monthly-report-print .banner .value-late { color: #b45309 !important; }
            .monthly-report-print .banner .value-miss { color: #b91c1c !important; }
            .monthly-report-print .banner .value-rate { color: #047857 !important; }
            .monthly-report-print .st-P  { color: #047857 !important; }
            .monthly-report-print .st-Ni { color: #0d9488 !important; }
            .monthly-report-print .st-L  { color: #b45309 !important; }
            .monthly-report-print .st-A  { color: #b91c1c !important; }
            .monthly-report-print .st-Md { color: #0369a1 !important; }
            .monthly-report-print .st-MC { color: #6b21a8 !important; }
            .monthly-report-print .st-MIA{ color: #be123c !important; }
            .monthly-report-print .st-Off,
            .monthly-report-print .st-NG { color: #64748b !important; }
            .monthly-report-print .st-blank { color: #cbd5e1 !important; }
            .monthly-report-print table.matrix tbody tr { page-break-inside: avoid; }
          }
          .print-only { display: none; }
        `}</style>

        <div className="monthly-report-print flex flex-col flex-1 overflow-hidden">
          {/* ============ HEADER + TOOLBAR ============ */}
          <DialogHeader className="px-5 pt-5 pb-3 no-print">
            <DialogTitle className="flex items-center justify-between gap-3 flex-wrap">
              <span className="truncate">{company.name} — Monthly Report</span>
              <div className="flex items-center gap-2">
                <Input type="month" value={month} onChange={(e)=>setMonth(e.target.value)}
                  className="bg-[#0a0e1a] border-slate-800 h-9 w-[160px] num"
                  data-testid="monthly-month-picker" />
                <Button size="sm" onClick={handlePrint}
                  className="bg-emerald-600 hover:bg-emerald-500 text-emerald-50"
                  data-testid="monthly-print-btn">
                  <FileText className="w-3.5 h-3.5 mr-1.5" /> Print / PDF
                </Button>
              </div>
            </DialogTitle>
          </DialogHeader>

          {/* Print-only header (hidden on screen). Shows brand + date. */}
          <div className="print-only" style={{padding:"0 0 8px 0"}}>
            <div style={{display:"flex", justifyContent:"space-between",
                         alignItems:"flex-start",
                         borderBottom:"2px solid #0f172a", paddingBottom:"8px"}}>
              <div>
                <div style={{fontSize:"14px", fontWeight:700, letterSpacing:"-0.01em"}}>
                  MERCY MANAGEMENT SYSTEM
                </div>
                <div style={{fontSize:"10px", color:"#475569", marginTop:"2px"}}>
                  Kedatangan · Monthly Attendance Report
                </div>
              </div>
              <div style={{textAlign:"right", fontSize:"10px", color:"#475569",
                           fontFamily:"DM Mono, monospace"}}>
                Printed: <strong style={{color:"#0f172a"}}>{todayLabel}</strong><br/>
                Licence JTKSM 928 · mercymy.com
              </div>
            </div>
            <div style={{textAlign:"center", margin:"10px 0 4px"}}>
              <div style={{fontSize:"18px", fontWeight:700, letterSpacing:"-0.01em"}}>
                {company.name.toUpperCase()}
              </div>
              <div style={{fontSize:"11px", color:"#475569",
                           fontFamily:"DM Mono, monospace"}}>
                Month: {monthLabel}
                {data ? `  ·  ${daysInMonth.length} days  ·  ${data.workers?.length || 0} workers` : ""}
              </div>
            </div>
          </div>

          {/* ============ SUMMARY BANNER ============ */}
          {data && (
            <div className="banner mx-5 mb-3 rounded-lg border border-slate-800 bg-[#0a0e1a] px-4 py-3
                            print:mx-0 print:border-2 print:border-slate-900 print:bg-slate-50 print:my-2">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-xs">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 label">Workers</div>
                  <div className="num text-base font-bold text-slate-100">{data.workers?.length || 0}</div>
                  <div className="text-[10px] text-slate-500">on roster</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-emerald-400 label">Entered Work</div>
                  <div className="num text-base font-bold text-emerald-300 value-ent" data-testid="monthly-entered-total">
                    {data.entered_total ?? 0}
                  </div>
                  <div className="text-[10px] text-slate-500">
                    P {sumCol("Present")} · Ni {sumCol("NG IN")}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-amber-400 label">Late</div>
                  <div className="num text-base font-bold text-amber-300 value-late">{data.late_total ?? 0}</div>
                  <div className="text-[10px] text-slate-500">
                    {data.avg_late_minutes ? `avg ${data.avg_late_minutes} min late` : "—"}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-red-400 label">Did Not Work</div>
                  <div className="num text-base font-bold text-red-300 value-miss">{data.missed_total ?? 0}</div>
                  <div className="text-[10px] text-slate-500">
                    A {sumCol("Absent")} · MIA {sumCol("MIA")} · MC {sumCol("MC")} · Md {sumCol("Medical")}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-emerald-400 label">Attendance Rate</div>
                  <div className="num text-base font-bold text-emerald-300 value-rate" data-testid="monthly-attendance-rate">
                    {data.attendance_rate ?? 0}%
                  </div>
                  <div className="text-[10px] text-slate-500">entered ÷ working days</div>
                </div>
              </div>
            </div>
          )}

          {/* ============ MATRIX TABLE ============ */}
          <div className="overflow-auto flex-1 px-5 pb-3 print:px-0 print:overflow-visible">
            {!data ? <div className="p-8 text-center text-slate-500 text-sm">Loading…</div> : (
              <table className="matrix w-full text-[11px] border-separate border-spacing-0">
                <thead className="text-slate-500 sticky top-0 bg-[#0f1626] z-10 print:bg-slate-900 print:text-white">
                  <tr>
                    <th className="text-left px-2 py-1.5 uppercase tracking-wider sticky left-0 bg-[#0f1626] z-20 min-w-[200px] border-b border-slate-800">
                      Worker
                    </th>
                    {daysInMonth.map((d) => (
                      <th key={d} className="px-1 py-1.5 num text-center w-7 border-b border-slate-800 font-normal">
                        {d}
                      </th>
                    ))}
                    <th className="px-2 py-1.5 uppercase tracking-wider num w-11 text-center border-b border-slate-800 border-l-2 border-slate-700 text-emerald-400">Ent</th>
                    <th className="px-2 py-1.5 uppercase tracking-wider num w-11 text-center border-b border-slate-800 text-amber-400">Late</th>
                    <th className="px-2 py-1.5 uppercase tracking-wider num w-11 text-center border-b border-slate-800 text-red-400">Miss</th>
                    <th className="px-2 py-1.5 uppercase tracking-wider num w-11 text-center border-b border-slate-800 text-slate-400">Off</th>
                    {SUMMARY_COLS.map((s) => (
                      <th key={s.key} title={s.key}
                          className={`px-1 py-1.5 uppercase tracking-wider num w-8 text-center border-b border-slate-800 ${s.cls}`}>
                        {s.short}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((w, i) => (
                    <tr key={w.id} className={`hover:bg-white/[0.02] ${w.is_terminated ? "bg-red-500/[0.04]" : ""}`}>
                      <td className="px-2 py-1.5 sticky left-0 bg-[#0f1626] border-b border-slate-900/70 truncate max-w-[200px]"
                          title={w.is_terminated
                            ? `Terminated ${w.terminated_at?.slice(0,10) || ""} · by ${w.terminated_by_name || "?"}${w.terminated_reason ? ` · ${w.terminated_reason}` : ""}`
                            : w.name}>
                        <span className="text-slate-600 num mr-2">{i+1}.</span>
                        <span className={w.is_terminated ? "line-through text-slate-400" : ""}>{w.name}</span>
                        {w.is_terminated && (
                          <span className="ml-1.5 text-[9px] uppercase tracking-wider px-1 py-0.5 rounded bg-red-500/15 text-red-300 border border-red-500/30 num">
                            Terminated {w.terminated_at?.slice(0,10) || ""}
                          </span>
                        )}
                      </td>
                      {daysInMonth.map((d) => {
                        const dateKey = `${month}-${String(d).padStart(2, "0")}`;
                        const status = w.days?.[dateKey];
                        const { ch, cls } = cellFor(status);
                        const stClass = status ? `st-${(status === "NG IN" ? "Ni" : status).replace(" ", "")}` : "st-blank";
                        return (
                          <td key={d}
                              title={status ? `${dateKey} · ${status}` : dateKey}
                              className={`text-center num text-[10px] leading-none py-1 border-b border-slate-900/70 ${cls} ${stClass}`}>
                            {ch}
                          </td>
                        );
                      })}
                      <td className="text-center num text-emerald-300 border-b border-slate-900/70 border-l-2 border-slate-700 font-bold value-ent"
                          data-testid={`row-entered-${i}`}>
                        {w.entered_work || 0}
                      </td>
                      <td className="text-center num text-amber-300 border-b border-slate-900/70 font-semibold value-late">
                        {w.late_total || 0}
                      </td>
                      <td className="text-center num text-red-300 border-b border-slate-900/70 font-semibold value-miss">
                        {w.missed_total || 0}
                      </td>
                      <td className="text-center num text-slate-400 border-b border-slate-900/70">
                        {w.off_total || 0}
                      </td>
                      {SUMMARY_COLS.map((s) => {
                        const n = w.counts[s.key] || 0;
                        return (
                          <td key={s.key}
                              className={`text-center num border-b border-slate-900/70 ${n > 0 ? s.cls : "text-slate-700"}`}>
                            {n || "·"}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td className="px-2 py-2 sticky left-0 bg-slate-900 text-slate-100 font-bold uppercase tracking-wider text-[10px] border-t-2 border-slate-700">
                      Total · {rows.length} workers
                    </td>
                    <td colSpan={daysInMonth.length}
                        className="text-center text-[9px] text-slate-500 bg-slate-900 border-t-2 border-slate-700 uppercase tracking-widest"
                        style={{letterSpacing: "0.2em"}}>
                      — daily matrix —
                    </td>
                    <td className="text-center num bg-slate-900 text-emerald-300 font-bold border-t-2 border-slate-700 border-l-2 value-ent">
                      {data.entered_total ?? 0}
                    </td>
                    <td className="text-center num bg-slate-900 text-amber-300 font-bold border-t-2 border-slate-700 value-late">
                      {data.late_total ?? 0}
                    </td>
                    <td className="text-center num bg-slate-900 text-red-300 font-bold border-t-2 border-slate-700 value-miss">
                      {data.missed_total ?? 0}
                    </td>
                    <td className="text-center num bg-slate-900 text-slate-300 font-bold border-t-2 border-slate-700">
                      {data.off_total ?? 0}
                    </td>
                    {SUMMARY_COLS.map((s) => (
                      <td key={s.key} className={`text-center num bg-slate-900 font-bold border-t-2 border-slate-700 ${s.cls}`}>
                        {sumCol(s.key)}
                      </td>
                    ))}
                  </tr>
                </tfoot>
              </table>
            )}
          </div>

          {/* ============ LEGEND ============ */}
          <div className="px-5 py-2 border-t border-slate-800 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-[10px] num">
            {[
              { key: "Present", short: "P", desc: "Present" },
              { key: "NG IN",   short: "Ni", desc: "Night Shift (NG IN)" },
              { key: "Late",    short: "L", desc: "Late" },
              { key: "Absent",  short: "A", desc: "Absent" },
              { key: "Medical", short: "Md", desc: "Medical" },
              { key: "MC",      short: "MC", desc: "MC (Sick note)" },
              { key: "MIA",     short: "MIA", desc: "MIA (no contact)" },
              { key: "Off",     short: "Off", desc: "Off-day" },
              { key: "NG",      short: "NG", desc: "NG (pre-marked)" },
            ].map((s) => (
              <span key={s.key} className="inline-flex items-center gap-1">
                <span className={`px-1.5 py-0.5 rounded text-[10px] border ${STATUS_CLASSES[s.key]}`}>
                  {s.short}
                </span>
                <span className="text-slate-400">{s.desc}</span>
              </span>
            ))}
          </div>

          {/* ============ FOOTER ============ */}
          {data && (
            <div className="px-5 py-3 border-t-2 border-slate-700 text-[10px] bg-[#0a0e1a]
                            print:bg-slate-50 print:border-slate-900">
              <div className="grid grid-cols-[140px_1fr] gap-x-3 gap-y-1 num text-slate-400 print:text-slate-700">
                <div className="uppercase tracking-wider text-slate-200 font-bold print:text-slate-900">
                  Recorded by
                </div>
                <div data-testid="monthly-recorded-by">{recordedByLine}</div>

                <div className="uppercase tracking-wider text-slate-200 font-bold print:text-slate-900">
                  Total updates
                </div>
                <div>
                  {data.total_records || 0} records this month
                  {data.last_updated_at && ` · last update ${new Date(data.last_updated_at).toLocaleString("en-GB", {
                    day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit"
                  })}`}
                </div>

                <div className="uppercase tracking-wider text-slate-200 font-bold print:text-slate-900">
                  Definition
                </div>
                <div>
                  "Entered Work" = Present + Night Shift IN ·
                  Late / MC / Medical / Absent / MIA each tracked separately
                </div>

                <div className="uppercase tracking-wider text-slate-200 font-bold print:text-slate-900">
                  Generated
                </div>
                <div>{todayLabel}, {new Date().toLocaleTimeString("en-GB", {hour:"2-digit", minute:"2-digit"})}</div>
              </div>
              <div className="text-center text-[10px] text-slate-500 mt-3 print:text-slate-600">
                <strong className="text-slate-300 print:text-slate-900">Mercy Consultancy</strong> · Licence JTKSM 928 · mercymy.com
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function WhatsAppDialog({ open, onOpenChange, companyId, companyName, date }) {
  const [text, setText] = useState("");
  useEffect(() => {
    if (!open || !companyId) return;
    (async () => {
      try {
        const { data } = await api.get(`/roster/companies/${companyId}/whatsapp`, { params: { date } });
        setText(data.text);
      } catch { setText(""); }
    })();
  }, [open, companyId, date]);
  const copy = async () => {
    try { await navigator.clipboard.writeText(text); toast.success("Copied to clipboard"); } catch {}
  };
  const downloadTxt = () => {
    const BOM = "\uFEFF";
    const blob = new Blob([BOM + text], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `attendance-${(companyName || "report").replace(/\s+/g, "_")}-${date}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    toast.success("Downloaded. Open the .txt, copy from there, paste into WhatsApp.",
      { duration: 6000 });
  };
  const shareWhatsApp = async () => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied. Open a WhatsApp chat and paste (Ctrl+V).",
        { duration: 6000 });
    } catch {
      toast.error("Couldn't copy — use the Copy button first.");
      return;
    }
    window.open("https://web.whatsapp.com/", "_blank", "noopener");
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0f1626] border-slate-800 text-slate-100 max-w-2xl max-h-[85vh] flex flex-col gap-3 p-4">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageCircle className="w-4 h-4 text-emerald-400" /> WhatsApp — {companyName}
          </DialogTitle>
        </DialogHeader>
        <div className="text-[11px] text-emerald-200/90 bg-emerald-500/10 border border-emerald-500/30 rounded px-2.5 py-1.5 leading-snug shrink-0">
          <b>If emojis don't render when you paste:</b> click <b>Download .txt</b> instead — opens a clean UTF-8 file in any editor. Copy from there and paste into WhatsApp. This bypasses the browser clipboard entirely.
        </div>
        <Textarea value={text} onChange={(e)=>setText(e.target.value)}
          className="bg-[#0a0e1a] border-slate-800 font-mono text-xs leading-relaxed flex-1 min-h-[180px] resize-none overflow-auto"
          data-testid="roster-whatsapp-text" />
        <DialogFooter className="shrink-0 pt-1 flex flex-row gap-2 sm:justify-end flex-wrap">
          <Button variant="outline" className="border-slate-700" onClick={copy}
            data-testid="roster-whatsapp-copy">Copy</Button>
          <Button variant="outline" className="border-slate-700" onClick={downloadTxt}
            data-testid="roster-whatsapp-download">
            <Download className="w-4 h-4 mr-1" /> Download .txt
          </Button>
          <Button className="bg-emerald-600 hover:bg-emerald-500" onClick={shareWhatsApp}
            data-testid="roster-whatsapp-share">
            <MessageCircle className="w-4 h-4 mr-1" /> WhatsApp
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
